import {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import * as FlexLayout from "flexlayout-react";
import ProtectedRoute from "../../common/components/routes/protectedRoute";
import UserProfile from "./../pages/userProfile";
import AuthAdmin from "../../admin/auth/authAdmin";
import ProductAdmin from "../../admin/products/productAdmin";
import MarketAdmin from "../../admin/markets/marketAdmin";
import TradingAdmin from "../../admin/trading/tradingAdmin";
import CompanyWhitelistPage from "../../trading/companyWhitelistPage";
import OrderReleaseLimitPage from "../../trading/orderReleaseLimitPage";
import ReviewTradesAuthWrapper from "./../../marketData/reviewTrades/reviewTradesAuthWrapper";
import SubmitData from "../../submitData/submitData";
import ReviewMarksAuthWrapper from "./../../marketData/reviewMarks/reviewMarksAuthWrapper";
import DashboardContainer from "./../../dashboards/dashboardContainer";
import TradingComponent from "./../../trading/tradingComponent";
import AlbertaEnvironmentalTrades from "../../dashboards/albertaEnvironmental/albertaEnvironmentalTrades";
import AlbertaEnvironmentalIssues from "../../dashboards/albertaEnvironmental/albertaEnvironmentalIssues";
import AlbertaEnvironmentalStatusChanges from "../../dashboards/albertaEnvironmental/albertaEnvironmentalStatusChanges";
import AlbertaEnvironmentalRegistry from "../../dashboards/albertaEnvironmental/albertaEnvironmentalRegistry";
import AlbertaPowerReport from "../../dashboards/albertaPowerReport/albertaPowerReport";
import BCEnvironmentalRegistry from "../../dashboards/bcEnvironmental/bcEnvironmentalRegistry";
import BCEnvironmentalIssuances from "../../dashboards/bcEnvironmental/bcEnvironmentalIssuances";
import BCEnvironmentalRetirements from "../../dashboards/bcEnvironmental/bcEnvironmentalRetirements";
import MarketData from "../../marketData/marketData";
import { useLocation, useNavigate } from "react-router-dom";
import FontAwesome from "react-fontawesome";

const findSelectedRouteInLayout = (node) => {
  if (!node) {
    return null;
  }

  if (node.type === "tabset") {
    const selectedIndex = typeof node.selected === "number" ? node.selected : 0;
    const selectedTab = node.children?.[selectedIndex] ?? node.children?.[0];
    return selectedTab?.config?.route ?? null;
  }

  for (const child of node.children ?? []) {
    const route = findSelectedRouteInLayout(child);
    if (route) {
      return route;
    }
  }

  return null;
};

const getRouteFromModel = (layoutModel) => {
  if (!layoutModel) {
    return null;
  }

  const activeTabset = layoutModel.getActiveTabset?.();
  const activeRoute = activeTabset?.getSelectedNode?.()?.getConfig?.()?.route;
  if (activeRoute) {
    return activeRoute;
  }

  const mainTabset = layoutModel.getNodeById?.("main");
  const mainRoute = mainTabset?.getSelectedNode?.()?.getConfig?.()?.route;
  if (mainRoute) {
    return mainRoute;
  }

  return findSelectedRouteInLayout(layoutModel.toJson?.().layout);
};

const FlexShell = (
  {
    userAccountId,
    theme,
    setTheme,
    allowData,
    isAdmin,
    isSubscriber,
    venues,
    termSchedules,
    defaultTabConfig,
  },
  ref,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  const STORAGE_KEY = "userLayout_v1" + userAccountId;

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);

    if (saved) {
      try {
        const json = JSON.parse(saved);
        setModel(FlexLayout.Model.fromJson(json));
        return;
      } catch (err) {
        console.warn("Failed to parse saved layout:", err);
        localStorage.removeItem(STORAGE_KEY); // clear bad layout
      }
    }

    // Always create a model, even if defaultTabConfig is null
    const INITIAL_LAYOUT = {
      global: {
        minWidth: 100,
        minHeight: 100,
        tabSetEnableMaximize: false,
        //tabEnableFloat: true, //This doesn't quite work right, but leaving it here because it enables the feature, the float windows just close automatically.
      },
      borders: [],
      layout: {
        type: "row",
        children: [
          {
            type: "tabset",
            id: "main",
            weight: 100,
            enableMaximize: false,
            enableDeleteWhenEmpty: false,
            children: defaultTabConfig ? [defaultTabConfig] : [],
          },
        ],
      },
    };

    setModel(FlexLayout.Model.fromJson(INITIAL_LAYOUT));
  }, [defaultTabConfig, STORAGE_KEY]);

  // --- Persist layout changes to localStorage
  const saveLayout = useCallback(
    (model) => {
      try {
        const json = model.toJson();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(json));
      } catch (err) {
        console.warn("Failed to save layout:", err);
      }
    },
    [STORAGE_KEY],
  );

  const [model, setModel] = useState(null);

  const [refreshMap, setRefreshMap] = useState({});

  const triggerTabRefresh = (tabId) => {
    setRefreshMap((prev) => ({ ...prev, [tabId]: Date.now() }));
  };

  const factory = useCallback(
    (node) => {
      const refreshKey = refreshMap[node.getId()];

      switch (node.getComponent()) {
        case "MarketData":
          const tabbedVenue =
            venues &&
            venues.find((x) => x.venueId === node.getConfig().market?.venueId);

          return (
            <ProtectedRoute
              permittedRoles={["api-query-marks", "api-query-trades"]}
            >
              <MarketData
                market={node.getConfig().market}
                venue={tabbedVenue}
                termSchedules={node.getConfig().termSchedules}
                productId={node.getConfig().productId}
                key={refreshKey}
              />
            </ProtectedRoute>
          );
        case "UserProfile":
          return (
            <ProtectedRoute>
              <UserProfile theme={theme} setTheme={setTheme} key={refreshKey} />
            </ProtectedRoute>
          );
        case "AuthAdmin":
          return (
            <ProtectedRoute permittedRoles={["administrator", "operator"]}>
              <AuthAdmin key={refreshKey} />
            </ProtectedRoute>
          );
        case "ProductAdmin":
          return (
            <ProtectedRoute permittedRoles={["administrator", "operator"]}>
              <ProductAdmin key={refreshKey} />
            </ProtectedRoute>
          );
        case "MarketAdmin":
          return (
            <ProtectedRoute permittedRoles={["administrator", "operator"]}>
              <MarketAdmin key={refreshKey} />
            </ProtectedRoute>
          );
        case "TradingAdmin":
          return (
            <ProtectedRoute permittedRoles={["administrator", "operator"]}>
              <TradingAdmin key={refreshKey} />
            </ProtectedRoute>
          );
        case "SubmitDataParentAuthWrapper":
          return (
            <ProtectedRoute
              permittedRoles={[
                "api-manage-marks",
                "api-manage-trades",
                "api-approve-marks",
                "api-approve-trades",
                "api-manage-external-data",
                "api-approve-external-data",
              ]}
            >
              <SubmitData
                market={node.getConfig().market}
                venues={venues}
                termSchedules={termSchedules}
                externalData={node.getConfig().externalData}
                key={refreshKey}
              />
            </ProtectedRoute>
          );
        case "ReviewMarksAuthWrapper":
          return (
            <ProtectedRoute permittedRoles={["api-approve-marks"]}>
              <ReviewMarksAuthWrapper key={refreshKey} />
            </ProtectedRoute>
          );
        case "ReviewTradesAuthWrapper":
          return (
            <ProtectedRoute permittedRoles={["api-approve-trades"]}>
              <ReviewTradesAuthWrapper key={refreshKey} />
            </ProtectedRoute>
          );
        case "AlbertaPowerReport":
          return (
            <ProtectedRoute
              permittedRoles={[
                "administrator",
                "operator",
                "energy-daily-subscriber",
              ]}
            >
              <AlbertaPowerReport
                allowData={allowData}
                isAdmin={isAdmin}
                key={refreshKey}
              />
            </ProtectedRoute>
          );
        case "DashboardContainerEnvironmental":
          return (
            <DashboardContainer
              fileName={`albertaEnvironmentalDashboard${theme}.html`}
              key={refreshKey}
            />
          );
        case "DashboardContainerElectricity":
          return (
            <DashboardContainer
              fileName={`albertaElectricityDashboard${theme}.html`}
              key={refreshKey}
            />
          );
        case "TradingComponent":
          return (
            <ProtectedRoute
              permittedRoles={[
                "administrator",
                "operator",
                "trader",
                "trading-company-administrator",
              ]}
            >
              <TradingComponent />
            </ProtectedRoute>
          );
        case "CompanyWhitelistPage":
          return (
            <ProtectedRoute
              permittedRoles={[
                "api-query-own-company-whitelists",
                "api-manage-own-company-whitelists",
                "api-query-company-whitelists",
                "api-manage-company-whitelists",
              ]}
            >
              <CompanyWhitelistPage key={refreshKey} />
            </ProtectedRoute>
          );
        case "OrderReleaseLimitPage":
          return (
            <ProtectedRoute
              permittedRoles={[
                "administrator",
                "operator",
                "api-manage-order-release-limits",
                "api-manage-own-order-release-limits",
              ]}
            >
              <OrderReleaseLimitPage key={refreshKey} />
            </ProtectedRoute>
          );
        case "AlbertaEnvironmentalTrades":
          return (
            <AlbertaEnvironmentalTrades
              isSubscriber={isSubscriber}
              key={refreshKey}
            />
          );
        case "AlbertaEnvironmentalRegistry":
          return (
            <AlbertaEnvironmentalRegistry
              isSubscriber={isSubscriber}
              key={refreshKey}
            />
          );
        case "AlbertaEnvironmentalIssues":
          return (
            <ProtectedRoute permittedRoles={["api-query-trades"]}>
              <AlbertaEnvironmentalIssues key={refreshKey} />
            </ProtectedRoute>
          );
        case "AlbertaEnvironmentalStatusChanges":
          return (
            <ProtectedRoute permittedRoles={["api-query-trades"]}>
              <AlbertaEnvironmentalStatusChanges key={refreshKey} />
            </ProtectedRoute>
          );
        case "BCEnvironmentalRegistry":
          return (
            <ProtectedRoute
              permittedRoles={[
                "api-query-external-data",
                "market-data-subscriber",
                "operator",
                "administrator",
              ]}
            >
              <BCEnvironmentalRegistry
                isSubscriber={isSubscriber}
                key={refreshKey}
              />
            </ProtectedRoute>
          );
        case "BCEnvironmentalIssuances":
          return (
            <ProtectedRoute
              permittedRoles={[
                "api-query-external-data",
                "market-data-subscriber",
                "operator",
                "administrator",
              ]}
            >
              <BCEnvironmentalIssuances key={refreshKey} />
            </ProtectedRoute>
          );
        case "BCEnvironmentalRetirements":
          return (
            <ProtectedRoute
              permittedRoles={[
                "api-query-external-data",
                "market-data-subscriber",
                "operator",
                "administrator",
              ]}
            >
              <BCEnvironmentalRetirements key={refreshKey} />
            </ProtectedRoute>
          );
        default:
          return <div>Unknown Component</div>;
      }
    },
    [
      allowData,
      isAdmin,
      isSubscriber,
      refreshMap,
      setTheme,
      termSchedules,
      theme,
      venues,
    ],
  );

  useImperativeHandle(ref, () => ({
    addTab(tabConfig) {
      let currentModel = model;
      try {
        currentModel.doAction(
          FlexLayout.Actions.addNode(
            {
              type: "tab",
              component: tabConfig.component,
              name: tabConfig.name,
              config: tabConfig,
              id: tabConfig.id,
            },
            "main",
            FlexLayout.DockLocation.CENTER,
            0,
            true,
          ),
        );

        setModel(FlexLayout.Model.fromJson(currentModel.toJson()));
      } catch (err) {
        currentModel.doAction(FlexLayout.Actions.selectTab(tabConfig.id));
        triggerTabRefresh(tabConfig.id);
        //Would be nice to flash the tab or something here, haven't figured it out yet.

        // Force React to notice the key change by re-setting the model
        setModel(FlexLayout.Model.fromJson(currentModel.toJson()));
      }
    },
  }));

  // --- Handle model change events
  const handleModelChange = useCallback(
    (newModel) => {
      // Save the updated layout
      saveLayout(newModel);

      // Handle navigation if tab has a route
      const route = getRouteFromModel(newModel);
      if (route) navigate(route);
    },
    [navigate, saveLayout],
  );

  useEffect(() => {
    const route = getRouteFromModel(model);

    if (route && route !== location.pathname) {
      navigate(route, { replace: true });
    }
  }, [model, location.pathname, navigate]);

  return (
    <div className="flex-container">
      {model && (
        <FlexLayout.Layout
          model={model}
          factory={factory}
          onModelChange={handleModelChange}
          factoryLazy={true}
          onRenderTab={(node, renderValues) => {
            const parent = node.getParent();
            const isActive =
              parent &&
              parent.getType() === "tabset" &&
              parent.getSelectedNode() === node;

            if (isActive) {
              renderValues.buttons.push(
                <button
                  key="refresh"
                  title="Refresh Tab"
                  className="flex-layout-refresh-button"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "inherit",
                    marginLeft: "4px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // prevent changing tabs
                    triggerTabRefresh(node.getId());
                  }}
                >
                  <FontAwesome name="refresh" />
                </button>,
              );
            }
          }}
        />
      )}
    </div>
  );
};

// Wrap in forwardRef, no HOC
export default forwardRef(FlexShell);
